import { ImgHTMLAttributes, ReactNode } from 'react';

import { BlueContainer, Heading, Button, type ButtonProps } from '@app/shared/ui/atoms/Common';
import { cn } from '@app/shared/utils/common';

type ImageProps = Omit<ImgHTMLAttributes<HTMLImageElement>, 'src' | 'alt'> & {
  src: string;
  alt: string;
};

interface ImageBannerWithCTAProps {
  title: ReactNode;
  imageProps: ImageProps;
  extraContent?: ReactNode;
  buttonProps: ButtonProps;
  topOffset?: number;
  rightOffset?: number;
  bottomOffset?: number;
}

export function ImageBannerWithCTA({
  title,
  imageProps,
  extraContent,
  buttonProps,
  topOffset = 48,
  rightOffset = 0,
  bottomOffset = 36,
}: ImageBannerWithCTAProps) {
  const { className: buttonClassName, ...buttonRest } = buttonProps;
  const { src: imageSrc, alt: imageAlt, className: imageClassName, ...imageRest } = imageProps;
  const titlePaddings = { paddingTop: `${topOffset}px`, paddingBottom: `${bottomOffset}px` };

  return (
    <BlueContainer withShadow className="rounded-2xl">
      <div
        className="relative flex"
        style={{
          top: `-${topOffset}px`,
          width: `calc(100% + ${rightOffset}px)`,
          marginBottom: `-${topOffset + bottomOffset}px`,
        }}
      >
        <Heading
          level={4}
          className="flex-1 overflow-hidden wrap-break-word leading-[1.2]"
          style={{ ...titlePaddings }}
        >
          {title}
        </Heading>
        <img
          src={imageSrc}
          alt={imageAlt}
          className={cn('w-1/2 object-contain', imageClassName)}
          {...imageRest}
        />
        {extraContent && <div style={{ ...titlePaddings }}>{extraContent}</div>}
      </div>

      <Button
        shadow="blue"
        className={cn('relative flex items-center justify-center gap-2', buttonClassName)}
        {...buttonRest}
      />
    </BlueContainer>
  );
}
